/* ==========================================================================
   Cart Drawer — oddit-cart-drawer
   Interactive behaviors: open/close, overlay click, escape key, focus trap
   ========================================================================== */

(function () {
  'use strict';

  const SELECTORS = {
    drawer: '.oddit-cart-drawer',
    overlay: '.oddit-cart-drawer__overlay',
    panel: '.oddit-cart-drawer__panel',
    closeBtn: '.oddit-cart-drawer__close',
  };

  const CLASSES = {
    open: 'is-open',
  };

  function initCartDrawer(container) {
    const drawer = container || document.querySelector(SELECTORS.drawer);
    if (!drawer) return;

    const overlay = drawer.querySelector(SELECTORS.overlay);
    const panel = drawer.querySelector(SELECTORS.panel);

    if (!overlay || !panel) return;

    let previouslyFocusedElement = null;
    let recommendationsTimer = null;
    let recommendationsIdleId = null;

    // Cache the empty-state collection cards from the initial (correct) render.
    // refreshDrawer() re-renders the drawer via the Section Rendering API, and a
    // section that lives in a section group can come back WITHOUT its block
    // config — which drops these cards. Restoring from this cache keeps the
    // empty-cart category cards showing every time, not intermittently.
    var cachedEmptyCollections = null;
    var initialCollections = panel.querySelector('.oddit-cart-drawer__collections');
    if (initialCollections) cachedEmptyCollections = initialCollections.outerHTML;

    function restoreEmptyStateCards() {
      var emptyState = panel.querySelector('.oddit-cart-drawer__empty-state');
      if (!emptyState || !cachedEmptyCollections) return;
      var collections = emptyState.querySelector('.oddit-cart-drawer__collections');
      if (collections && collections.children.length) return; // cards already present
      if (collections) {
        collections.outerHTML = cachedEmptyCollections;
      } else {
        emptyState.insertAdjacentHTML('beforeend', cachedEmptyCollections);
      }
    }

    /**
     * Open the cart drawer
     */
    function openDrawer() {
      // The cart page is already a full cart view — popping the drawer over it is
      // redundant and let the two carts drift out of sync (removing an item on the
      // page left stale items in the drawer). Suppress it there.
      if (document.body.classList.contains('template-cart')) return;

      previouslyFocusedElement = document.activeElement;
      var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = scrollbarWidth + 'px';
      drawer.removeAttribute('inert');
      drawer.classList.add(CLASSES.open);
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus the close button after transition
      setTimeout(function () {
        var closeBtn = drawer.querySelector(SELECTORS.closeBtn);
        if (closeBtn) closeBtn.focus();
      }, 310);

      // Listen for escape key
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('focusin', handleFocusIn);

      // Recommendations are non-critical. Do not fetch/parse them during page
      // startup; wait until somebody actually opens the cart, and then wait
      // until the opening transition has finished before doing the work.
      scheduleCartRecommendations();
    }

    /**
     * Close the cart drawer
     */
    function closeDrawer() {
      drawer.classList.remove(CLASSES.open);
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';

      // Return focus to the element that opened the drawer
      if (previouslyFocusedElement) {
        previouslyFocusedElement.focus();
        previouslyFocusedElement = null;
      }

      // Keep the closed drawer out of the keyboard tab order; aria-hidden
      // alone hides it from screen readers but leaves its controls focusable.
      drawer.setAttribute('inert', '');

      // Remove escape key listener
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusIn);
    }

    /**
     * Handle keyboard events
     */
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }

      // Focus trap within the drawer panel
      if (event.key === 'Tab') {
        tabBackward = event.shiftKey;
        tabbing = true;
        setTimeout(function () { tabbing = false; }, 0);
        trapFocus(event);
      }
    }

    var tabbing = false;
    var tabBackward = false;

    // Only controls that are actually shown count as the first/last stop;
    // hidden ones (collapsed sections, empty-cart blocks) let focus slip out.
    function getFocusable() {
      return Array.prototype.filter.call(
        panel.querySelectorAll('a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, [tabindex]:not([tabindex="-1"])'),
        function (el) {
          return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
        }
      );
    }

    // Backstop for stops the list can't see (e.g. Redo's checkout button in a
    // closed shadow root): if a Tab still lands outside, bring focus back.
    // Only Tab moves are redirected, so app popups can still take focus.
    function handleFocusIn(event) {
      if (!tabbing || panel.contains(event.target)) return;
      const focusableElements = getFocusable();
      if (focusableElements.length === 0) return;
      focusableElements[tabBackward ? focusableElements.length - 1 : 0].focus();
    }

    /**
     * Trap focus within the drawer panel
     */
    function trapFocus(event) {
      const focusableElements = getFocusable();

      if (focusableElements.length === 0) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    }

    /**
     * Re-render the panel using Shopify's section_id approach (same pattern as base theme)
     */
    function refreshDrawer() {
      var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return fetch(rootUrl + 'cart?section_id=oddit-cart-drawer')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newPanel = doc.querySelector(SELECTORS.panel);
          if (newPanel) panel.innerHTML = newPanel.innerHTML;
          restoreEmptyStateCards();
          scheduleCartRecommendations();
        });
    }

    function scheduleCartRecommendations() {
      if (recommendationsTimer !== null || recommendationsIdleId !== null) return;

      recommendationsTimer = window.setTimeout(function () {
        recommendationsTimer = null;

        if ('requestIdleCallback' in window) {
          recommendationsIdleId = window.requestIdleCallback(
            function () {
              recommendationsIdleId = null;
              populateCartRecommendations();
            },
            { timeout: 1000 }
          );
        } else {
          recommendationsTimer = window.setTimeout(function () {
            recommendationsTimer = null;
            populateCartRecommendations();
          }, 0);
        }
      }, 350);
    }

    function populateCartRecommendations() {
      var fallback = panel.querySelector('[data-cart-rec-fallback]');
      if (!fallback) return;
      var grid = fallback.querySelector('[data-cart-rec-grid]');
      if (!grid || grid.children.length) return;
      var productId = fallback.getAttribute('data-cart-rec-product-id');
      if (!productId) return;

      var cartIds = (fallback.getAttribute('data-cart-product-ids') || '').split(',');
      var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var url =
        rootUrl +
        'recommendations/products?product_id=' +
        productId +
        '&intent=related&limit=6&section_id=oddit-cart-recommendations';

      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = doc.querySelectorAll('.oddit-cart-drawer__upsell-card');
          var added = 0;
          cards.forEach(function (card) {
            if (added >= 2) return;
            var pid = card.getAttribute('data-product-id');
            if (pid && cartIds.indexOf(pid) !== -1) return; // already in cart
            grid.appendChild(card);
            added += 1;
          });
          if (added > 0) fallback.hidden = false;
        })
        .catch(function () {});
    }

    /**
     * Immediately update DOM visibility based on cart API response.
     * Acts as a guaranteed fallback if refreshDrawer() fails.
     */
    function applyCartState(cart, key, quantity) {
      if (quantity === 0) {
        var lineItem = panel.querySelector('.oddit-cart-drawer__line-item[data-line-item-key="' + key + '"]');
        if (lineItem) lineItem.parentNode.removeChild(lineItem);
      }

      var itemsState = panel.querySelector('[data-cart-items]');
      var emptyState = panel.querySelector('.oddit-cart-drawer__empty-state');
      var footer = drawer.querySelector('.oddit-cart-drawer__footer');
      var shippingBanner = drawer.querySelector('.oddit-cart-drawer__shipping-banner');

      if (cart.item_count === 0) {
        if (itemsState) itemsState.style.display = 'none';
        if (emptyState) emptyState.style.display = '';
        if (footer) footer.style.display = 'none';
        if (shippingBanner) shippingBanner.style.display = 'none';
        restoreEmptyStateCards();
      }
    }

    /**
     * Disable/enable interactive buttons during fetch
     */
    function setLoading(isLoading) {
      panel.querySelectorAll(
        '.oddit-cart-drawer__quantity-btn, .oddit-cart-drawer__remove-btn, .oddit-cart-drawer__rec-add'
      ).forEach(function (btn) {
        btn.disabled = isLoading;
      });
    }

    /**
     * POST to /cart/change.js then refresh the drawer
     */
    function cartChange(key, quantity) {
      var lineItem = panel.querySelector('.oddit-cart-drawer__line-item[data-line-item-key="' + key + '"]');
      if (lineItem) lineItem.classList.add('is-pending');
      setLoading(true);
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity }),
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          applyCartState(cart, key, quantity);
          return refreshDrawer().finally(function () {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          });
        })
        .finally(function () {
          setLoading(false);
          panel.querySelectorAll('.is-pending').forEach(function (el) {
            el.classList.remove('is-pending');
          });
        });
    }

    /**
     * POST to /cart/add.js then refresh the drawer
     */
    function cartAdd(variantId) {
      setLoading(true);
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then(function (r) { return r.json(); })
        .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
        .then(function (cart) {
          return refreshDrawer().then(function () {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          });
        })
        .finally(function () {
          setLoading(false);
          panel.querySelectorAll('.is-pending').forEach(function (el) {
            el.classList.remove('is-pending');
          });
        });
    }

    /**
     * Cleanup event listeners
     */
    function destroy() {
      overlay.removeEventListener('click', closeDrawer);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusIn);

      if (recommendationsTimer !== null) {
        clearTimeout(recommendationsTimer);
        recommendationsTimer = null;
      }
      if (recommendationsIdleId !== null && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(recommendationsIdleId);
        recommendationsIdleId = null;
      }
    }

    // Delegated click handler for close, qty, remove, and upsell actions
    drawer.addEventListener('click', function (e) {
      if (e.target.closest(SELECTORS.closeBtn)) { closeDrawer(); return; }

      var actionBtn = e.target.closest('[data-action]');
      if (actionBtn) {
        var action = actionBtn.getAttribute('data-action');
        var key = actionBtn.getAttribute('data-line-item-key');
        if (action === 'remove') {
          cartChange(key, 0);
        } else if (action === 'decrease' || action === 'increase') {
          var qty = parseInt(actionBtn.getAttribute('data-quantity'), 10);
          if (!isNaN(qty) && qty >= 0) cartChange(key, qty);
        }
        return;
      }

      var upsellBtn = e.target.closest('.oddit-cart-drawer__rec-add');
      if (upsellBtn) {
        var variantId = upsellBtn.getAttribute('data-variant-id');
        if (variantId) cartAdd(variantId);
      }
    });

    overlay.addEventListener('click', closeDrawer);

    // Recommendations intentionally do not run on page load. They are queued
    // only after the cart drawer is opened.

    // Show/hide shipping banner based on cart state
    document.addEventListener('cart:updated', function (e) {
      var shippingBanner = drawer.querySelector('.oddit-cart-drawer__shipping-banner');
      if (!shippingBanner) return;
      var itemCount = e.detail && e.detail.cart && e.detail.cart.item_count;
      shippingBanner.style.display = itemCount > 0 ? '' : 'none';
    });

    // Expose open/close methods globally for external triggers (e.g. cart icon button)
    window.odditCartDrawer = {
      open: openDrawer,
      close: closeDrawer,
      toggle: function () {
        if (drawer.classList.contains(CLASSES.open)) {
          closeDrawer();
        } else {
          openDrawer();
        }
      },
      refresh: refreshDrawer,
      destroy: destroy,
    };
  }

  // Initialize on DOM ready
  initCartDrawer();

  // Theme editor support: re-initialize when section is loaded/reloaded
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector(SELECTORS.drawer);
    if (section) {
      initCartDrawer(section);
    }
  });

  document.addEventListener('shopify:section:unload', function () {
    if (window.odditCartDrawer && typeof window.odditCartDrawer.destroy === 'function') {
      window.odditCartDrawer.destroy();
    }
  });

  /* Quick-shop toggle handled globally by oddit-collection-product-card.js */
})();